"use client";

import React from "react";
import { Building2, Ticket, TicketX, User, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  canCreateTicketsAtLevel,
  getAccessLevelLabel,
} from "@/lib/auth/access-levels";
import type { AccessLevel } from "@/lib/types/database";
import type {
  ClientAccessRow,
  CompanyClientAccessRow,
} from "@/lib/hooks/api/admin/sections/client-access";

export interface ClientAccessGrantsSummaryProps {
  companyGrants: CompanyClientAccessRow[];
  contactGrants: ClientAccessRow[];
  isLoading?: boolean;
  showRevoke?: boolean;
  onRevokeCompany?: (companyId: string) => void;
  onRevokeContact?: (contactId: string) => void;
  revokeCompanyPending?: boolean;
  revokeContactPending?: boolean;
  onOpenCompany?: (companyId: string) => void;
  onOpenContact?: (contactId: string) => void;
  emptyMessage?: string;
}

function AccessLevelBadge({ level }: { level: string }) {
  const label = getAccessLevelLabel(level);
  const variant =
    level === "read_only"
      ? "secondary"
      : level === "limited"
        ? "outline"
        : "default";

  return (
    <Badge variant={variant} className="shrink-0 text-xs font-normal">
      {label}
    </Badge>
  );
}

function TicketCapability({ level }: { level: string }) {
  const canCreate = canCreateTicketsAtLevel(level as AccessLevel);
  const Icon = canCreate ? Ticket : TicketX;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs ${
        canCreate ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {canCreate ? "Can create tickets" : "View only — no new tickets"}
    </span>
  );
}

export function ClientAccessGrantsSummary({
  companyGrants,
  contactGrants,
  isLoading = false,
  showRevoke = false,
  onRevokeCompany,
  onRevokeContact,
  revokeCompanyPending = false,
  revokeContactPending = false,
  onOpenCompany,
  onOpenContact,
  emptyMessage = "No client portal access granted yet.",
}: ClientAccessGrantsSummaryProps) {
  const activeContactGrants = contactGrants.filter((g) => g.is_active !== false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }

  const hasAny = companyGrants.length > 0 || activeContactGrants.length > 0;

  if (!hasAny) {
    return (
      <p className="text-sm text-muted-foreground leading-relaxed">{emptyMessage}</p>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground leading-relaxed border-b pb-3">
        Client portal data comes from two grant types.{" "}
        <strong className="font-medium text-foreground">Company-wide</strong> grants
        every contact at a company (including contacts added later).{" "}
        <strong className="font-medium text-foreground">Single-contact</strong> grants
        hide all other people at that company.
      </p>

      {companyGrants.length > 0 && (
        <section aria-labelledby="company-wide-grants-heading">
          <div className="mb-2 flex items-start gap-2">
            <Building2
              className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
              aria-hidden
            />
            <div>
              <h4
                id="company-wide-grants-heading"
                className="text-sm font-semibold"
              >
                Company-wide access
              </h4>
              <p className="text-xs text-muted-foreground">
                All contacts at these companies — current and future
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {companyGrants.map((grant) => (
              <li
                key={`${grant.user_id}:${grant.company_id}`}
                className="rounded-md border border-blue-200/80 bg-blue-50/50 px-3 py-2.5 dark:border-blue-900/50 dark:bg-blue-950/20"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    {onOpenCompany ? (
                      <button
                        type="button"
                        onClick={() => onOpenCompany(grant.company_id)}
                        className="truncate text-sm font-medium text-left hover:underline"
                      >
                        {grant.company?.name || grant.company_id}
                      </button>
                    ) : (
                      <p className="truncate text-sm font-medium">
                        {grant.company?.name || grant.company_id}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <AccessLevelBadge level={grant.access_level} />
                      <TicketCapability level={grant.access_level} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tickets, communications, and time entries for every contact
                      at this company appear in the client portal.
                    </p>
                  </div>
                  {showRevoke && onRevokeCompany && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => onRevokeCompany(grant.company_id)}
                      disabled={revokeCompanyPending}
                    >
                      <X className="mr-1 h-4 w-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {activeContactGrants.length > 0 && (
        <section aria-labelledby="single-contact-grants-heading">
          <div className="mb-2 flex items-start gap-2">
            <User
              className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400"
              aria-hidden
            />
            <div>
              <h4
                id="single-contact-grants-heading"
                className="text-sm font-semibold"
              >
                Single-contact access
              </h4>
              <p className="text-xs text-muted-foreground">
                Only the named contact — not their whole company
              </p>
            </div>
          </div>
          <ul className="space-y-2">
            {activeContactGrants.map((grant) => (
              <li
                key={grant.id}
                className="rounded-md border border-violet-200/80 bg-violet-50/40 px-3 py-2.5 dark:border-violet-900/50 dark:bg-violet-950/20"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    {onOpenContact ? (
                      <button
                        type="button"
                        onClick={() => onOpenContact(grant.contact_id)}
                        className="truncate text-sm font-medium text-left hover:underline"
                      >
                        {grant.contact?.name || grant.contact_id}
                      </button>
                    ) : (
                      <p className="truncate text-sm font-medium">
                        {grant.contact?.name || grant.contact_id}
                      </p>
                    )}
                    <p className="truncate text-xs text-muted-foreground">
                      {grant.contact?.email}
                      {grant.contact?.company_name
                        ? ` · ${grant.contact.company_name}`
                        : ""}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <AccessLevelBadge level={grant.access_level ?? "limited"} />
                      <TicketCapability level={grant.access_level ?? "limited"} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only this person&apos;s tickets and timeline — coworkers at
                      the same company stay hidden.
                    </p>
                  </div>
                  {showRevoke && onRevokeContact && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() => onRevokeContact(grant.contact_id)}
                      disabled={revokeContactPending}
                    >
                      <X className="mr-1 h-4 w-4" />
                      Revoke
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
